import type { FC } from 'react';
import clsx from 'clsx';
import { EmptyState } from '@/components/shared/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { useTranslation } from '@/hooks/useTranslation';
import { formatNumber, formatProbability, formatWear } from '../lib/wearFormat';
import type { WearOutput, WearQuery } from '../type/WearTypes';

/** Planned pieces above this probability of being worn get the warning colour. */
const RISKY_P_WORN = 0.1;

/** What the tool can still mill at the next operation's settings (prudent and median). */
export const WearCapacityCard: FC<{ output: WearOutput; query: WearQuery }> = ({
  output,
  query,
}) => {
  const { t, language } = useTranslation();
  const next = output.next;

  const tile = (kind: 'prudent' | 'median', primary: boolean) => {
    if (!next) return null;
    const key = kind === 'prudent' ? 'p10' : 'p50';
    const pieces = next.piecesLeft?.[key];
    const volume = next.volumeLeft[key];
    return (
      <Card
        tier="inset"
        padding="sm"
        className={clsx('flex flex-col gap-1', primary && 'border-primary/30 bg-primary/5')}
        data-testid={`wear-capacity-${kind}`}
      >
        <span className="eyebrow">{t(`mlWear.result.${kind}`)}</span>
        {pieces !== undefined ? (
          <>
            <span className="text-3xl font-extrabold tabular-nums">
              {pieces}
              <span className="ml-1.5 text-sm font-medium text-muted-foreground">
                {t('mlWear.result.piecesUnit')}
              </span>
            </span>
            <span className="text-sm tabular-nums text-muted-foreground">
              {formatNumber(language, volume, 2)} cm³
            </span>
          </>
        ) : (
          <span className="text-3xl font-extrabold tabular-nums">
            {formatNumber(language, volume, 2)}
            <span className="ml-1.5 text-sm font-medium text-muted-foreground">cm³</span>
          </span>
        )}
        <span className="text-xs text-muted-foreground">{t(`mlWear.result.${kind}Help`)}</span>
      </Card>
    );
  };

  return (
    <Card as="section" className="flex flex-col gap-4" data-testid="wear-capacity">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-semibold">{t('mlWear.result.capacityTitle')}</h2>
          {next && output.skipped > 0 && (
            <Badge tone="warning" size="sm" data-testid="wear-capacity-overestimated">
              {t('mlWear.result.overestimatedTag')}
            </Badge>
          )}
        </div>
        {next && query.nextFz !== undefined && query.nextAe !== undefined && (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge className="whitespace-normal tabular-nums">
              {t('mlWear.result.capacitySettings', {
                fz: formatNumber(language, query.nextFz, 3),
                ae: formatNumber(language, query.nextAe, 2),
                d: formatNumber(language, query.diameter, 1),
                hm: formatNumber(language, next.hm, 1),
              })}
            </Badge>
            {next.outOfRange.length > 0 && (
              <Badge tone="warning" size="sm">
                {t('mlWear.result.outOfRangeTag')}
              </Badge>
            )}
          </span>
        )}
      </div>
      {!next ? (
        <EmptyState
          size="compact"
          title={t('mlWear.result.capacityEmpty')}
          data-testid="wear-capacity-empty"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {tile('prudent', false)}
            {tile('median', true)}
          </div>
          {!next.piecesLeft && (
            <p className="text-xs text-muted-foreground">
              {t('mlWear.result.needsVolumePerPiece')}
            </p>
          )}
          {next.planned && (
            <Callout
              tone={
                next.planned.wear >= 1
                  ? 'error'
                  : next.planned.pWorn > RISKY_P_WORN
                    ? 'warning'
                    : 'success'
              }
              role={null}
              data-testid="wear-planned"
            >
              <p>
                {t('mlWear.result.planned', {
                  pieces: next.planned.pieces,
                  volume: formatNumber(language, next.planned.volume, 2),
                  wear: formatWear(language, next.planned.wear),
                  pWorn: formatProbability(language, next.planned.pWorn),
                })}{' '}
                <span className={clsx(next.planned.pWorn > RISKY_P_WORN && 'font-semibold')}>
                  {t(
                    next.planned.pWorn > RISKY_P_WORN
                      ? 'mlWear.result.plannedRisky'
                      : 'mlWear.result.plannedSafe'
                  )}
                </span>
              </p>
            </Callout>
          )}
        </>
      )}
    </Card>
  );
};
